import classNames from "classnames";
import {cssInterop} from "nativewind";
import type React from "react";
import {useCallback, useMemo} from "react";
import type {ColorValue, TextStyle, ViewStyle} from "react-native";
import Dropdown from "react-native-input-select";

import {memoWithType} from "../../react";
import {ChevronDownIcon} from "../../react/components/Icons/ChevronDownIcon";
import {CloseIcon} from "../../react/components/Icons/CloseIcon";
import {isGroupOption} from "../Select/util";
import type {SelectGenericProps, TValue} from "../SelectGeneric/types";

import {sortSelectedItemsToTop} from "./sortSelectedItemsToTop";

type Props<T extends TValue = TValue> = SelectGenericProps<T> & {
    testID: string;
    placeholder?: string;
    isSearchable?: boolean;
    className?: string;
};
type ReactNativeInputSelectProps = React.ComponentProps<typeof Dropdown>;
type ParamsOnChangeValue = Parameters<React.ComponentProps<typeof Dropdown>["onValueChange"]>[0];
type StyleExposeProps = {
    checkboxStyle?: ViewStyle;
    checkboxLabelStyle?: TextStyle;
    textInputStyle?: ViewStyle;
    sectionHeaderStyle?: TextStyle;
    checkboxUnselectedColor?: ColorValue;
    placeholderTextColor?: ColorValue;
};

const DropdownSupportClasses = cssInterop(
    ({
        checkboxStyle,
        checkboxUnselectedColor,
        checkboxLabelStyle,
        textInputStyle,
        placeholderTextColor,
        sectionHeaderStyle,
        ...props
    }: ReactNativeInputSelectProps & StyleExposeProps) => {
        return (
            <Dropdown
                {...props}
                checkboxControls={{checkboxStyle, checkboxUnselectedColor, checkboxLabelStyle}}
                listComponentStyles={{
                    sectionHeaderStyle
                }}
                searchControls={{
                    textInputStyle: {
                        paddingVertical: 0,
                        paddingHorizontal: 0,
                        ...textInputStyle
                    },
                    textInputProps: {
                        placeholderTextColor
                    }
                }}
            />
        );
    },
    {
        primaryColorClassName: {
            target: false,
            nativeStyleToProp: {
                color: "primaryColor"
            }
        },
        selectedItemClassName: "selectedItemStyle",
        labelClassName: "labelStyle",
        dropdownIconClassName: "dropdownIconStyle",
        dropdownClassName: "dropdownStyle",
        placeholderClassName: "placeholderStyle",
        dropdownContainerClassName: "dropdownContainerStyle",
        multipleSelectedItemClassName: "multipleSelectedItemStyle",
        checkboxClassName: "checkboxStyle",
        checkboxUnselectedColorClassName: {
            target: false,
            nativeStyleToProp: {
                color: "checkboxUnselectedColor"
            }
        },
        checkboxLabelClassName: "checkboxLabelStyle",
        searchTextInputClassName: "textInputStyle",
        searchPlaceholderColorClassName: {
            target: false,
            nativeStyleToProp: {
                color: "placeholderTextColor"
            }
        },
        sectionHeaderTitleClassName: "sectionHeaderStyle"
    }
);

const ReactNativeInputSelect = <T extends TValue = TValue>({
    testID,
    options,
    placeholder,
    isMulti,
    value,
    isSearchable = true,
    className,
    onChange
}: Props<T>) => {
    const optionsFormatted = useMemo(() => {
        if (isGroupOption(options)) {
            return options.map(option => ({
                title: option.label || "",
                data: option.options.map(o => ({
                    label: o.label,
                    value: o.value,
                    disabled: isMulti ? o.isFixed : undefined
                }))
            }));
        }

        const sortedOptions = sortSelectedItemsToTop(options, value, option => option.value);

        return sortedOptions.map(option => ({
            label: option.label,
            value: option.value,
            disabled: isMulti ? option.isFixed : undefined
        }));
    }, [options, value, isMulti]);

    // flattern
    const flatOptions = useMemo(() => {
        return optionsFormatted.flatMap(option => {
            return "data" in option ? option.data : option;
        });
    }, [optionsFormatted]);

    const handleValueChange = useCallback(
        (value: ParamsOnChangeValue) => {
            if (isMulti && Array.isArray(value)) {
                onChange?.(value as T[]);
                return;
            }

            if (!isMulti && !Array.isArray(value)) {
                const option = flatOptions.find(elem => elem.value === value);
                if (option) {
                    onChange?.(value as T, option);
                }
            }
        },
        [isMulti, flatOptions, onChange]
    );

    return (
        <DropdownSupportClasses
            checkboxClassName={classNames("border-blueGray-200 bg-primary-700", {
                "rounded-sm": isMulti,
                "rounded-full": !isMulti
            })}
            checkboxLabelClassName="text-blueGray-800 text-xs"
            checkboxUnselectedColorClassName="text-white"
            dropdownContainerClassName={`
                relative
                border
                border-solid
                border-blueGray-200
                rounded-sm
                py-2
                px-3
                min-h-9
                flex-shink-0
                flex-grow-0
                mb-0
                ${className}
            `}
            dropdownIcon={<ChevronDownIcon fill="blueGray.800" size="xs" />}
            dropdownIconClassName="absolute right-1 top-1/2 -translate-y-1/2 bg-white"
            isMultiple={isMulti}
            isSearchable={isSearchable}
            listControls={{
                hideSelectAll: true,
                keyboardShouldPersistTaps: "handled"
            }}
            modalControls={{
                modalOptionsContainerStyle: {
                    // this fixes the modal disappearing when keyboard is open
                    flex: 1
                },
                modalProps: {
                    supportedOrientations: [
                        "portrait",
                        "portrait-upside-down",
                        "landscape",
                        "landscape-left",
                        "landscape-right"
                    ]
                }
            }}
            multipleSelectedItemClassName={`
                px-1
                py-px
                gap-0.5
                bg-blueGray-50
                border border-solid border-blueGray-200
                rounded-xs
                text-xs
                color-blueGray-800
            `}
            multipleSelectedItemStyle={{
                paddingHorizontal: 0,
                paddingVertical: 0
            }}
            options={optionsFormatted}
            placeholder={placeholder}
            primaryColorClassName="text-primary-600"
            searchPlaceholderColorClassName="text-blueGray-400"
            searchTextInputClassName={`
                border-blueGray-200
                rounded-sm
                min-h-10
                px-3
                py-2
                bg-white
            `}
            sectionHeaderTitleClassName="text-blueGray-600"
            selectedItemClassName="text-blueGray-800 pr-2"
            selectedItemsControls={{
                removeItemIcon: <CloseIcon fill="blueGray.800" size="2xs" />
            }}
            selectedValue={value}
            testID={testID}
            onValueChange={handleValueChange}
        />
    );
};

export default memoWithType(ReactNativeInputSelect);
